"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { IoChevronDown, IoChevronUp } from "react-icons/io5"

/**
 * TimePicker Component - A customizable time picker with up/down arrows
 * 
 * Props:
 * @param {string} value - Selected time in "HH:mm" (24-hour) or "hh:mm AM/PM" (12-hour) format
 * @param {function} onChange - Callback function called when time is selected (receives time string)
 * @param {boolean} format24h - Whether to use 24-hour format (default: false for 12-hour)
 * @param {number} minuteStep - How many minutes to jump on arrow click (default: 1)
 * @param {boolean} disabled - Whether the picker is disabled (default: false)
 * @param {string} className - Additional CSS classes for styling
 * @param {string} placeholder - Placeholder text when no time is selected (default: "Select time")
 * 
 * Usage Examples:
 * 12-hour format: <TimePicker value="02:30 PM" onChange={(time) => setTime(time)} />
 * 24-hour format: <TimePicker value="14:30" format24h={true} onChange={(time) => setTime(time)} />
 * With minute step: <TimePicker minuteStep={15} onChange={(time) => setTime(time)} />
 */
export function TimePicker({
  value = "",
  onChange,
  format24h = false,
  minuteStep = 1,
  disabled = false,
  className = "",
  placeholder = "Select time",
  isDarkMode = false,
  minTime = null, // Format: "HH:mm" for 24h or "hh:mm A" for 12h
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [hours, setHours] = useState(format24h ? 0 : 12)
  const [minutes, setMinutes] = useState(0)
  const [period, setPeriod] = useState("AM")
  const debounceTimeoutRef = useRef(null)
  const hasUserInteracted = useRef(false)

  // Parse initial value
  useEffect(() => {
    if (value) {
      if (format24h) {
        // 24-hour format: "HH:mm"
        const [h, m] = value.split(":").map(Number)
        setHours(h || 0)
        setMinutes(m || 0)
      } else {
        // 12-hour format: "hh:mm AM/PM"
        const timeRegex = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i
        const match = value.match(timeRegex)
        if (match) {
          setHours(Number.parseInt(match[1]) || 12)
          setMinutes(Number.parseInt(match[2]) || 0)
          setPeriod(match[3].toUpperCase())
        }
      }
    } else {
      // Reset to default values when value is empty
      setHours(format24h ? 0 : 12)
      setMinutes(0)
      setPeriod("AM")
      hasUserInteracted.current = false
    }
  }, [value, format24h])

  // Helper function to check if time is valid
  const isTimeValid = (h, m, p) => {
    if (!minTime) return true
    
    let currentTime, minTimeValue
    
    if (format24h) {
      currentTime = h * 60 + m
      const [minH, minM] = minTime.split(":").map(Number)
      minTimeValue = minH * 60 + minM
    } else {
      currentTime = h * 60 + m
      if (p === "PM" && h !== 12) currentTime += 12 * 60
      if (p === "AM" && h === 12) currentTime -= 12 * 60
      
      const timeRegex = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i
      const match = minTime.match(timeRegex)
      if (match) {
        let [minH, minM, minP] = [Number.parseInt(match[1]), Number.parseInt(match[2]), match[3].toUpperCase()]
        minTimeValue = minH * 60 + minM
        if (minP === "PM" && minH !== 12) minTimeValue += 12 * 60
        if (minP === "AM" && minH === 12) minTimeValue -= 12 * 60
      } else {
        return true
      }
    }
    
    return currentTime >= minTimeValue
  }

  const incrementHours = () => {
    hasUserInteracted.current = true
    if (format24h) {
      const newHours = (hours + 1) % 24
      if (isTimeValid(newHours, minutes, period)) {
        setHours(newHours)
      }
    } else {
      const newHours = hours === 12 ? 1 : hours === 11 ? 12 : hours + 1
      const newPeriod = hours === 11 ? (period === "AM" ? "PM" : "AM") : period
      if (isTimeValid(newHours, minutes, newPeriod)) {
        setHours(newHours)
        if (hours === 11) {
          setPeriod(newPeriod)
        }
      }
    }
  }

  const decrementHours = () => {
    hasUserInteracted.current = true
    if (format24h) {
      const newHours = hours === 0 ? 23 : hours - 1
      if (isTimeValid(newHours, minutes, period)) {
        setHours(newHours)
      }
    } else {
      const newHours = hours === 1 ? 12 : hours === 12 ? 11 : hours - 1
      const newPeriod = hours === 12 ? (period === "AM" ? "PM" : "AM") : period
      if (isTimeValid(newHours, minutes, newPeriod)) {
        setHours(newHours)
        if (hours === 12) {
          setPeriod(newPeriod)
        }
      }
    }
  }

  const incrementMinutes = () => {
    hasUserInteracted.current = true
    setMinutes((prev) => {
      const newMinutes = prev + minuteStep
      if (newMinutes >= 60) {
        // Wrap to next hour
        let newHours, newPeriod = period
        if (format24h) {
          newHours = (hours + 1) % 24
        } else {
          if (hours === 12) {
            newHours = 1
          } else if (hours === 11) {
            newPeriod = period === "AM" ? "PM" : "AM"
            newHours = 12
          } else {
            newHours = hours + 1
          }
        }
        
        if (isTimeValid(newHours, newMinutes - 60, newPeriod)) {
          setHours(newHours)
          if (!format24h && hours === 11) {
            setPeriod(newPeriod)
          }
          return newMinutes - 60
        }
        return prev
      }
      
      if (isTimeValid(hours, newMinutes, period)) {
        return newMinutes
      }
      return prev
    })
  }

  const decrementMinutes = () => {
    hasUserInteracted.current = true
    setMinutes((prev) => {
      const newMinutes = prev - minuteStep
      if (newMinutes < 0) {
        // Wrap to previous hour
        let newHours, newPeriod = period
        if (format24h) {
          newHours = hours === 0 ? 23 : hours - 1
        } else {
          if (hours === 1) {
            newHours = 12
          } else if (hours === 12) {
            newPeriod = period === "AM" ? "PM" : "AM"
            newHours = 11
          } else {
            newHours = hours - 1
          }
        }
        
        if (isTimeValid(newHours, 60 + newMinutes, newPeriod)) {
          setHours(newHours)
          if (!format24h && hours === 12) {
            setPeriod(newPeriod)
          }
          return 60 + newMinutes
        }
        return prev
      }
      
      if (isTimeValid(hours, newMinutes, period)) {
        return newMinutes
      }
      return prev
    })
  }

  const togglePeriod = () => {
    hasUserInteracted.current = true
    const newPeriod = period === "AM" ? "PM" : "AM"
    if (isTimeValid(hours, minutes, newPeriod)) {
      setPeriod(newPeriod)
    }
  }

  // Format display value
  const getDisplayValue = useCallback(() => {
    // Show empty only if no value is provided AND user hasn't interacted
    if (!value && !hasUserInteracted.current) {
      return ""
    }
    
    // If user has interacted, show internal state
    if (hasUserInteracted.current) {
      if (format24h) {
        return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`
      } else {
        return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${period}`
      }
    }
    
    // If value is provided, show it
    if (value) {
      if (format24h) {
        return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`
      } else {
        return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${period}`
      }
    }
    
    return ""
  }, [hours, minutes, period, format24h, value])

  // Watch for state changes and update parent with debounce
  useEffect(() => {
    if (onChange && hasUserInteracted.current) {
      // Clear existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
      
      // Set new timeout
      debounceTimeoutRef.current = setTimeout(() => {
        // Generate time string from internal state, not from getDisplayValue
        let timeString
        if (format24h) {
          timeString = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`
        } else {
          timeString = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${period}`
        }
        
        if (timeString) {
          onChange(timeString)
        }
      }, 100) // 100ms debounce
    }
    
    // Cleanup timeout on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [hours, minutes, period, format24h, onChange])

  const handleInputClick = () => {
    if (!disabled) {
      // Set default time when input is clicked for the first time
      if (!hasUserInteracted.current && !value) {
        hasUserInteracted.current = true
        
        if (minTime) {
          // Parse minTime and set as default
          if (format24h) {
            const [h, m] = minTime.split(":").map(Number)
            setHours(h || 0)
            setMinutes(m || 0)
          } else {
            const timeRegex = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i
            const match = minTime.match(timeRegex)
            if (match) {
              setHours(Number.parseInt(match[1]) || 12)
              setMinutes(Number.parseInt(match[2]) || 0)
              setPeriod(match[3].toUpperCase())
            } else {
              setHours(12)
              setMinutes(0)
              setPeriod("AM")
            }
          }
        } else {
          setHours(12)
          setMinutes(0)
          setPeriod("AM")
        }
      }
      setIsOpen(!isOpen)
    }
  }

  return (
    <>
      <style jsx>{`
        @keyframes windowsMinimizeIn {
          0% {
            opacity: 0;
            transform: translateY(15px) scale(0.9);
            transform-origin: top center;
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes windowsMinimizeOut {
          0% {
            opacity: 1;
            transform: translateY(0) scale(1);
            transform-origin: top center;
          }
          100% {
            opacity: 0;
            transform: translateY(15px) scale(0.9);
          }
        }
        
        @keyframes overlayFadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        
        @keyframes overlayFadeOut {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        
        .time-picker-overlay {
          animation: overlayFadeIn 0.1s ease-out forwards;
        }
        
        .time-picker-overlay.closing {
          animation: overlayFadeOut 0.1s ease-out forwards;
        }
      `}</style>
      <div className={`relative ${className}`}>
      <input
        type="text"
        value={getDisplayValue()}
        placeholder={!value ? placeholder : ""}
        readOnly
        disabled={disabled}
        onClick={handleInputClick}
        className={`${className} custom-input-padding`}
        style={{
          width: '100%',
          height: '42px',
          padding: '0.5rem !important',
          paddingLeft: '0.5rem !important',
          paddingRight: '0.5rem !important',
          paddingTop: '0.5rem !important',
          paddingBottom: '0.5rem !important',
          border: '0.5px solid #e6e6e6',
          borderRadius: '5px',
          fontSize: '14px',
          fontFamily: 'Gilroy-Regular',
          backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
          color: isDarkMode ? '#ffffff' : '#000000',
          cursor: 'pointer'
        }}
      />

      {isOpen && !disabled && (
        <div 
          className={`absolute top-full -left-1.5 lg:-left-5 mt-1 border border-gray-300 rounded-md shadow-lg p-2 ${isDarkMode ? 'bg-[#2a2a2a] border-gray-600' : 'bg-white border-gray-300'}`} 
          style={{ 
            zIndex: 2147483647, 
            position: 'absolute',
            animation: 'windowsMinimizeIn 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards',
            transformOrigin: 'top center'
          }}
        >
          <div className="flex items-center justify-center space-x-2">
            {/* Hours */}
            <div className="flex flex-col items-center">
              <button
                type="button"
                onClick={incrementHours}
                className={`p-0.5 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <IoChevronUp className="w-5 h-5 text-orange-600" />
              </button>
              <div className={`w-8 text-center py-1 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {format24h ? hours.toString().padStart(2, "0") : hours.toString().padStart(2, "0")}
              </div>
              <button
                type="button"
                onClick={decrementHours}
                className={`p-0.5 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <IoChevronDown className="w-5 h-5 text-orange-600" />
              </button>
            </div>

            <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>:</div>

            {/* Minutes */}
            <div className="flex flex-col items-center">
              <button
                type="button"
                onClick={incrementMinutes}
                className={`p-0.5 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <IoChevronUp className="w-5 h-5 text-orange-600" />
              </button>
              <div className={`w-8 text-center py-1 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {minutes.toString().padStart(2, "0")}
              </div>
              <button
                type="button"
                onClick={decrementMinutes}
                className={`p-0.5 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <IoChevronDown className="w-5 h-5 text-orange-600" />
              </button>
            </div>

            {/* AM/PM for 12-hour format */}
            {!format24h && (
              <>
                <div className="text-sm font-medium dark:text-white"></div>
                <div className="flex flex-col items-center">
                  <button
                    type="button"
                    onClick={togglePeriod}
                    className={`p-0.5 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                  >
                    <IoChevronUp className="w-5 h-5 text-orange-600" />
                  </button>
                  <div className={`w-8 text-center py-1 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{period}</div>
                  <button
                    type="button"
                    onClick={togglePeriod}
                    className={`p-0.5 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                  >
                    <IoChevronDown className="w-5 h-5 text-orange-600" />
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Close button */}
          {/* <div className="mt-2 flex justify-center">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Done
            </button>
          </div> */}
        </div>
      )}

      {/* Overlay to close picker when clicking outside */}
      {isOpen && <div className="fixed inset-0 time-picker-overlay" style={{ zIndex: 2147483646 }} onClick={() => setIsOpen(false)} />}
      </div>
    </>
  )
}
